import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import * as Dialog from '@radix-ui/react-dialog';
import { RefreshCw, Server, Monitor, Package, Loader2, ArrowUpCircle } from 'lucide-react';
import Layout from '../../components/Layout';
import { Card, CardContent } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { adminAPI } from '../../api';
import { useToast } from '../../hooks/useToast';

const TABS = [
  { id: 'server', label: 'Server', icon: Server },
  { id: 'client', label: 'Client', icon: Monitor },
];

function StatusBadge({ status }) {
  const config = {
    'up-to-date': { variant: 'success', label: 'Up to date' },
    'update-available': { variant: 'warning', label: 'Update available' },
    unknown: { variant: 'neutral', label: 'Unknown' },
  };
  const { variant, label } = config[status] || config.unknown;
  return <Badge variant={variant}>{label}</Badge>;
}

function UpdateConfirmModal({ open, onOpenChange, target, packages, onConfirm, loading }) {
  const targetLabel = target === 'server' ? 'Server' : target === 'client' ? 'Client' : 'Server & Client';
  const count = packages?.length ?? 0;

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/50" />
        <Dialog.Content className="fixed top-1/2 left-1/2 z-50 w-full max-w-md -translate-x-1/2 -translate-y-1/2 overflow-hidden rounded-lg border border-[var(--ds-border-subtle)] bg-white dark:bg-gray-800 shadow-[var(--ds-shadow-popover)]">
          <div className="p-6">
            <Dialog.Title className="text-h2 font-semibold text-[var(--ds-text-primary)]">
              Confirm dependency update
            </Dialog.Title>
            <p className="mt-2 text-body text-[var(--ds-text-secondary)]">
              You are about to update {count} package{count !== 1 ? 's' : ''} in the <strong>{targetLabel}</strong> project.
            </p>
            <p className="mt-3 text-meta text-[var(--ds-text-muted)]">
              The server may need to be restarted after updating server dependencies. Client dependencies require a rebuild.
            </p>
            <div className="mt-6 flex justify-end gap-3">
              <Button variant="secondary" onClick={() => onOpenChange(false)} disabled={loading}>
                Cancel
              </Button>
              <Button variant="primary" onClick={onConfirm} disabled={loading}>
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Updating...
                  </>
                ) : (
                  'Update'
                )}
              </Button>
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

export default function AdminDependencies() {
  const navigate = useNavigate();
  const { success, error: toastError, ToastContainer } = useToast();

  const [activeTab, setActiveTab] = useState('server');
  const [data, setData] = useState({ server: [], client: [], clientUnavailable: false });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [updating, setUpdating] = useState(false);
  const [confirmModal, setConfirmModal] = useState({
    open: false,
    target: 'server',
    packages: [],
  });

  const fetchDependencies = async () => {
    try {
      setLoading(true);
      setError('');
      const res = await adminAPI.getDependencies();
      setData({
        server: res.server || [],
        client: res.client || [],
        clientUnavailable: !!res.clientUnavailable,
      });
    } catch (err) {
      // API returns 503 with error message when deps unavailable (e.g. deployment layout)
      setError(err.response?.data?.error || err.message || 'Failed to load dependencies');
      console.error('Error fetching dependencies:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDependencies();
  }, []);

  const deps = activeTab === 'server' ? data.server : data.client;
  const outdatedCount = deps.filter((d) => d.status === 'update-available').length;

  const openUpdateModal = (target, packages) => {
    setConfirmModal({ open: true, target, packages });
  };

  const closeUpdateModal = () => {
    if (!updating) setConfirmModal((p) => ({ ...p, open: false }));
  };

  const runUpdate = async () => {
    const { target, packages } = confirmModal;
    setUpdating(true);
    try {
      const res = await adminAPI.updateDependencies({
        target,
        packages: packages.length ? packages.map((p) => (typeof p === 'string' ? p : p.name)) : undefined,
      });
      const updated = res.updated || [];
      const errs = res.errors || [];
      if (updated.length > 0) {
        success(
          'Dependencies updated',
          `${updated.length} package${updated.length !== 1 ? 's' : ''} updated.${res.serverRestartNote ? ` ${res.serverRestartNote}` : ''}`
        );
        await fetchDependencies();
      }
      if (errs.length > 0) {
        toastError('Some updates failed', errs.map((e) => `${e.package}: ${e.error}`).join('; '));
      }
      closeUpdateModal();
    } catch (err) {
      toastError('Update failed', err.response?.data?.error || err.message || 'Could not update dependencies');
    } finally {
      setUpdating(false);
    }
  };

  const handleUpdateAll = () => {
    const toUpdate = deps.filter((d) => d.status === 'update-available');
    if (toUpdate.length === 0) {
      toastError('No updates', 'No outdated packages to update.');
      return;
    }
    openUpdateModal(activeTab, toUpdate);
  };

  const handleUpdateOne = (dep) => {
    openUpdateModal(activeTab, [dep]);
  };

  return (
    <Layout>
      {/* Header */}
      <div className="bg-[var(--ds-surface-panel)] border-b border-[var(--ds-border-subtle)] shadow-[var(--ds-shadow-card)]">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="md:flex md:items-center md:justify-between">
            <div className="flex-1 min-w-0">
              <h1 className="text-display text-[var(--ds-text-primary)] font-bold">
                Dependencies
              </h1>
              <p className="mt-1 text-meta text-[var(--ds-text-secondary)]">
                View and update server and client package versions
              </p>
            </div>
            <div className="mt-4 flex md:mt-0 md:ml-4 gap-3">
              <Button variant="secondary" onClick={() => navigate('/admin/dashboard')}>
                ← Back to Dashboard
              </Button>
              <Button variant="secondary" onClick={fetchDependencies} disabled={loading}>
                <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        {error && (
          <div className="mb-6 bg-[var(--ds-status-danger-bg)] border border-[var(--ds-status-danger)] text-[var(--ds-status-danger)] px-4 py-3 rounded-[var(--ds-radius-md)]">
            {error}
          </div>
        )}

        {data.clientUnavailable && (
          <div className="mb-6 bg-[var(--ds-status-warning-bg)] border border-[var(--ds-status-warning)] text-[var(--ds-status-warning)] px-4 py-3 rounded-[var(--ds-radius-md)]">
            Client dependencies are not available in this deployment (client folder may be built separately). Only server dependencies are shown.
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          {TABS.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setActiveTab(id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-[var(--ds-radius-md)] text-body font-medium transition-colors ${
                activeTab === id
                  ? 'bg-[var(--ds-accent-primary)] text-white'
                  : 'bg-[var(--ds-control-field-bg)] text-[var(--ds-text-secondary)] hover:bg-[var(--ds-control-field-bg-hover)] hover:text-[var(--ds-text-primary)]'
              }`}
            >
              <Icon className="h-4 w-4" />
              {label}
            </button>
          ))}
        </div>

        {/* Table Card */}
        <Card className="overflow-hidden">
          <CardContent className="p-0">
            <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--ds-border-divider)]">
              <h2 className="text-h2 font-semibold text-[var(--ds-text-primary)]">
                {activeTab === 'server' ? 'Server' : 'Client'} packages
              </h2>
              {outdatedCount > 0 && (
                <Button variant="primary" size="sm" onClick={handleUpdateAll} disabled={loading}>
                  <ArrowUpCircle className="h-4 w-4 mr-2" />
                  Update all outdated ({outdatedCount})
                </Button>
              )}
            </div>

            {loading ? (
              <div className="p-12 text-center">
                <Loader2 className="h-10 w-10 animate-spin text-[var(--ds-accent-primary)] mx-auto" />
                <p className="mt-2 text-meta text-[var(--ds-text-muted)]">Loading dependencies...</p>
              </div>
            ) : deps.length === 0 ? (
              <div className="p-12 text-center text-[var(--ds-text-muted)]">
                <Package className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>No dependencies found</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full">
                  <thead>
                    <tr className="border-b border-[var(--ds-border-divider)]">
                      <th className="px-6 py-3 text-left text-meta text-[var(--ds-text-muted)] uppercase tracking-wider">
                        Package
                      </th>
                      <th className="px-6 py-3 text-left text-meta text-[var(--ds-text-muted)] uppercase tracking-wider">
                        Current
                      </th>
                      <th className="px-6 py-3 text-left text-meta text-[var(--ds-text-muted)] uppercase tracking-wider">
                        Latest
                      </th>
                      <th className="px-6 py-3 text-left text-meta text-[var(--ds-text-muted)] uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-right text-meta text-[var(--ds-text-muted)] uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {deps.map((dep) => (
                      <tr
                        key={dep.name}
                        className="border-b border-[var(--ds-border-divider)] hover:bg-[var(--ds-control-field-bg)]"
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            <span className="text-body font-medium text-[var(--ds-text-primary)]">
                              {dep.name}
                            </span>
                            {dep.type === 'devDependencies' && (
                              <Badge variant="neutral" className="text-micro">dev</Badge>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-body text-[var(--ds-text-secondary)]">
                          {dep.currentVersion}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-body text-[var(--ds-text-secondary)]">
                          {dep.latestVersion ?? '—'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <StatusBadge status={dep.status} />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right">
                          {dep.status === 'update-available' && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleUpdateOne(dep)}
                              disabled={updating}
                            >
                              <ArrowUpCircle className="h-4 w-4 mr-1" />
                              Update
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Confirmation Modal */}
      <UpdateConfirmModal
        open={confirmModal.open}
        onOpenChange={closeUpdateModal}
        target={confirmModal.target}
        packages={confirmModal.packages}
        onConfirm={runUpdate}
        loading={updating}
      />

      <ToastContainer />
    </Layout>
  );
}
